import { startCase } from 'lodash';

import Workflow, { WorkflowFacetOptions } from 'models/Workflow';

import { VALUE_NAMES } from './constants';

// The `coverages` value extraction produces (OLIO-2586): one entry per insurance the packet
// listed. It's the only produced value that isn't a scalar, so it's read through here rather than
// inline. It is a SNAPSHOT of what extraction read — the referral's live coverages are the facets
// minted from it, which a person can then correct, add to, or archive (see `liveCoverages`).

export const COVERAGES_VALUE = 'coverages';

// Keys are camelCase. The value NAME (`coverages`) is snake_case and read verbatim, but the value
// CONTENT is deep-camelCased by olio-api's OliveBranch middleware on the way out — so what's
// stored as `payer_name` arrives here as `payerName`. Reading the stored spelling silently yields
// undefined and the coverage renders blank, which is exactly what happened first time round.
export interface ExtractedCoverage {
  payerName?: string;
  memberId?: string;
  // 'primary' | 'secondary' — kept open because a third is a schema addition on the API side, not
  // a change here. Single word, so camelCasing leaves it alone.
  sequence?: string;
}

// Narrowed defensively: the model types values as scalars while this one holds an array of
// objects, so anything malformed reads as no coverages rather than throwing mid-render.
//
// Module-private, like `liveCoverages` below: `currentCoverages` is the one read a surface should
// make, because it alone carries the rule for choosing between the two.
function readCoverages(workflow: Workflow): ExtractedCoverage[] {
  const raw = workflow.valueFor(COVERAGES_VALUE)?.value as unknown;
  if (!Array.isArray(raw)) return [];

  return raw.filter((entry): entry is ExtractedCoverage => !!entry && typeof entry === 'object');
}

// "Primary" / "Secondary" — how a coverage is named on screen. Falls back to nothing when the
// entry never carried a sequence, so a label is never invented for it.
export function coverageLabel(coverage: ExtractedCoverage): string {
  return coverage.sequence ? startCase(coverage.sequence) : '';
}

// Which insurance a live coverage is, as a word: its own label when the API gave it one, else the
// word its rank stands for — rank IS which insurance it is (OLIO-3141), so a referral holding only
// a secondary says Secondary. Past the vocabulary it is numbered ("coverage 3") rather than left
// blank: a third coverage is real (re-extracting a corrected packet mints one), and an unsuffixed
// "Payer Name" column would sit beside the legacy "Plan Type" one reading as the same thing.
// Shared by the extraction card and the Edit Demographics headings so the two name it alike.
export function coverageSequence(facet: { label: string | null; position: number }): string {
  return facet.label ?? COVERAGE_SEQUENCES[facet.position] ?? `coverage ${facet.position + 1}`;
}

// The referral's LIVE coverages in the snapshot's shape, primary first: one entry per unarchived
// coverage facet, its payer name and member id read scoped to that coverage (a coverage-scoped
// value is never on the referral itself, so an unscoped read misses it entirely).
function liveCoverages(workflow: Workflow): ExtractedCoverage[] {
  const scoped = (name: string, facetId: string): string | undefined => {
    const raw = workflow.valueFor(name, { facetId })?.value;
    return typeof raw === 'string' && raw ? raw : undefined;
  };

  return workflow.coverageFacets.map((facet) => ({
    payerName: scoped(VALUE_NAMES.PAYER_NAME, facet.id),
    memberId: scoped(VALUE_NAMES.MEMBER_ID, facet.id),
    sequence: coverageSequence(facet),
  }));
}

// What a read-only surface should show as the referral's insurance: the live coverages once the
// referral has any coverage facets, else the extraction snapshot.
//
// Live first because the snapshot is frozen at extraction and nothing rewrites it — a payer a
// person corrected, a secondary they added from Edit Demographics (OLIO-3332), or a coverage they
// archived all exist only as facets. The extraction card read the snapshot and so showed a
// referral whose header chip said "Secondary: Aetna" with no Secondary on the card under it.
//
// The snapshot is still the fallback for a referral that never had coverages minted as facets —
// one from before facets existed holds only the snapshot, and it still has to render. Decided on
// whether any coverage facet EXISTS, archived or not, rather than on whether any is live: archiving
// a referral's only coverage (reachable through the API today, not this app) is a deliberate
// removal, and falling back to the snapshot would put that insurance straight back on the card.
export function currentCoverages(workflow: Workflow): ExtractedCoverage[] {
  const minted = workflow.facets.some((facet) => facet.dimension === 'coverage');
  return minted ? liveCoverages(workflow) : readCoverages(workflow);
}

// The coverages the entry form offers, in order. Extraction asks the packet for these same two by
// name, so a typed set and an extracted set describe the same thing.
export const COVERAGE_SEQUENCES = ['primary', 'secondary'] as const;

// One row of the insurance step, before it becomes a coverage. Always strings — an untouched
// input is '' rather than undefined, which is what lets the blank rows drop out below.
export interface CoverageEntry {
  payerName: string;
  memberId: string;
}

export const emptyCoverageEntries = (): CoverageEntry[] =>
  COVERAGE_SEQUENCES.map(() => ({ payerName: '', memberId: '' }));

// The typed rows as the stored `coverages` value: blank rows dropped, blank fields omitted rather
// than sent as '', and each row stamped with the sequence its position means. Omitting is what
// keeps "typed a secondary payer but not its member id" from storing an empty member id that
// reads as a real one downstream.
//
// Sent camelCase on purpose — olio-api's OliveBranch snake_cases request bodies, so `payerName`
// is stored as `payer_name`, the same spelling extraction writes.
// Whether the typed rows describe the same insurance twice, in the way the engine can't tell apart.
// A coverage is identified by member id and payer name TOGETHER, so an identical pair is ONE
// coverage: minting keeps the first and drops the second. Only both fields matching counts —
// sharing just a payer, or just a member id, is two real coverages (one subscriber can hold two
// plans on one id), and referral intake declares an acceptance criteria for exactly that.
//
// Deliberately not exhaustive. A row holding only a payer name will also absorb a later row that
// adds a member id to the same payer, because the fuller identity recognises the partial one. That
// merge is wanted when extraction learns a member id it didn't have at first; catching it here
// would warn about the case the identity rules exist to handle.
export function duplicatesOneInsurance(entries: CoverageEntry[]): boolean {
  const coverages = coveragesFromEntries(entries);
  if (coverages.length < 2) return false;

  return new Set(coverages.map(coverageIdentity)).size < coverages.length;
}

// What makes two coverages the same insurance: payer name and member ID TOGETHER, case- and
// whitespace-insensitive. Both, because one subscriber can hold two plans on one member id and two
// payers can share a name across plans — either alone is two real coverages, and warning on it would
// cry wolf on an ordinary secondary. Shared by the create form (`duplicatesOneInsurance`) and the
// Edit Demographics slots so the two surfaces can't drift on what counts as a duplicate.
export function coverageIdentity(coverage: { payerName?: string; memberId?: string }): string {
  return [coverage.payerName?.trim() ?? '', coverage.memberId?.trim() ?? ''].join('|').toLowerCase();
}

export function coveragesFromEntries(entries: CoverageEntry[]): ExtractedCoverage[] {
  return COVERAGE_SEQUENCES.flatMap((sequence, index) => {
    const payerName = entries[index]?.payerName.trim() ?? '';
    const memberId = entries[index]?.memberId.trim() ?? '';
    if (!payerName && !memberId) return [];

    return [{ ...(payerName ? { payerName } : {}), ...(memberId ? { memberId } : {}), sequence }];
  });
}

// One insurance slot on the Edit Demographics insurance step (OLIO-3332): the rank, and the
// coverage filling it when one does.
//
// The step used to render the coverages a referral HAS, so a packet that named only a primary gave
// no way to add the secondary — the coordinator had to leave for the eligibility slideout's
// Secondary tab. Rendering slots instead means Secondary is always on screen, empty or filled.
//
// Deliberately NOT the same slots as the tab strip's `coverageTabs`, which has its own rule (never
// fewer tabs than coverages, numbered tabs past Secondary) for its own reason: the strip has to be
// able to reach every coverage, while this form only has to offer the two the vocabulary names.
// Sharing one helper would have tied each surface to the other's rule.
export interface CoverageSlot {
  position: number;
  facet: WorkflowFacetOptions | null;
  // Whether an empty slot may be filled in. True only for the ranks the vocabulary names, so the
  // form never invites a third coverage UX hasn't named (an open question on OLIO-2794).
  creatable: boolean;
  // Identifies the slot in markup. The rank for every ordinary one, so a form handle reads as the
  // rank it stands for; suffixed only for a coverage that had to be appended past its rank below.
  key: string;
}

// The slots to render for a referral. A slot is a RANK, never a place in the coverage list: the
// engine ranks a coverage by its own primary/secondary marking, so a packet naming only a secondary
// leaves rank 0 empty, and counting list places labelled that secondary "Primary" (OLIO-3141).
//
// Empty for a referral with no coverages at all. One pinned to a configuration from before coverages
// existed doesn't declare the `coverage` dimension, and the API rejects a create against it every
// time — so it is offered nothing rather than an invitation that cannot be accepted. Same guard the
// tab strip uses.
export function coverageSlots(workflow: Workflow): CoverageSlot[] {
  const coverages = workflow.coverageFacets;
  if (coverages.length === 0) return [];

  // The two the vocabulary names, plus any rank a coverage actually occupies — so Secondary is
  // always offered, and a coverage past it is still reachable rather than dropped off the form.
  const ranks = [...new Set([...COVERAGE_SEQUENCES.map((_, index) => index), ...coverages.map((c) => c.position)])];

  const claimed = new Set<string>();
  const ranked = ranks
    .sort((a, b) => a - b)
    .map((position) => {
      const facet = coverages.find((coverage) => coverage.position === position && !claimed.has(coverage.id)) ?? null;
      if (facet) claimed.add(facet.id);

      return { position, facet, creatable: position < COVERAGE_SEQUENCES.length, key: String(position) };
    });

  // Any coverage no rank reached — two sharing one, which the model can produce by defaulting a
  // missing position to 0. Appended rather than dropped: this form is the only place a coverage's
  // payer name and member ID can be corrected, and ranks alone left the second one uneditable while
  // still offering to create a third in the slot it should have occupied. Never creatable: it is
  // occupied by definition. The tab strip floors its own count for the same reason.
  const unreached = coverages
    .filter((coverage) => !claimed.has(coverage.id))
    .map((facet, index) => ({
      position: facet.position,
      facet,
      creatable: false,
      key: `${facet.position}.${index + 1}`,
    }));

  return [...ranked, ...unreached];
}

// "Primary Insurance" / "Secondary Insurance" — how a slot is headed on screen. The coverage's own
// label when the API gave it one, else the word its rank stands for, so an EMPTY slot is still
// named. Past the vocabulary a coverage is numbered rather than labelled: its own label would put a
// payer name where Primary and Secondary sit.
export function coverageSlotHeading(slot: CoverageSlot): string {
  return `${startCase(coverageSequence({ label: slot.facet?.label ?? null, position: slot.position }))} Insurance`;
}
